declare function gtag(...args: any[]): void;
/* path: frontend/src/pages/VideoPlayer.tsx (improved)
   - TikTok/YouTube safe-zone guides with crop preview
   - Share helper (Web Share API fallback to copy)
   - FormatEditor modal to re-render with new layout/explanation overrides
   - "Open in editor" button → /renderEditor?url=...
   - Improved error handling, accessibility, and visual design
*/
import { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Copy,
  Share2,
  Download,
  RotateCw,
  ExternalLink,
  Edit3,
  Play,
  AlertCircle
} from "lucide-react";
import SafeZoneOverlay from "../components/SafeZoneOverlay";
import { shareVideo } from "@/lib/share";
import FormatEditor from "@/components/FormatEditor";

// ───────────────── types ─────────────────
type Probe = {
  ok: boolean;
  status?: number;
  contentType?: string | null;
  message?: string;
};

type GuideMode = "off" | "9:16" | "16:9";

type SocialMetadata = {
  title?: string;
  description?: string;
  short_caption?: string;
  long_caption?: string;
  hashtags?: string[];
  og_title?: string;
  og_description?: string;
  thumbnail_alt?: string;
};

// ───────────────── component ─────────────────
export default function VideoPlayer() {
  const location = useLocation() as any;

  // Signed GCS URLs must never pass through the browser query string.
  const initialUrl: string | null = location?.state?.url || null;

  const social: SocialMetadata =
    location?.state?.social_metadata ||
    location?.state?.social ||
    {};

  const socialTitle =
    social.og_title ||
    social.title ||
    "Generated Quiz Video";

  const socialDescription =
    social.og_description ||
    social.description ||
    "Interactive educational quiz video created with AutoQuiz AI.";

  const socialHashtags =
    Array.isArray(social.hashtags) && social.hashtags.length > 0
      ? social.hashtags
          .map((tag) => String(tag).replace(/^#/, "").trim())
          .filter(Boolean)
      : ["quiz", "study", "education", "AutoQuizAI"];

  const shareText =
    social.short_caption ||
    social.description ||
    socialDescription;

  const [url, setUrl] = useState<string | null>(initialUrl);

  const [probe, setProbe] = useState<Probe>({ ok: false });
  const [isProbing, setIsProbing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [version, setVersion] = useState<number>(0); // force <video> reload on retry
  const [isPlaying, setIsPlaying] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Guides & format editor
  const [guides, setGuides] = useState<GuideMode>("off");
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    document.title = `${socialTitle} | AutoQuiz AI`;
  }, [socialTitle]);


  // Keyboard shortcuts: Space = play/pause, ⌘/Ctrl+C = copy URL
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!url) return;

      // Ignore if user is typing in an input
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
        return;
      }

      if (e.code === "Space") {
        e.preventDefault();
        togglePlayPause();
      } else if (e.key.toLowerCase() === "c" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        void handleCopy();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [url, isPlaying]);

  const togglePlayPause = () => {
    const v = videoRef.current;
    if (!v) return;

    if (v.paused) {
      void v.play().catch(() => {
        toast.error("Failed to play video");
      });
    } else {
      v.pause();
    }
  };

  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("✅ Copied video URL");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
        toast.success("✅ Copied video URL");
      } catch {
        toast.error("Copy failed");
      }
    }
  };

  const handleShare = async () => {
    if (!url) return;

    try {
      if (navigator.share) {
        await navigator.share({
          title: socialTitle,
          text: shareText,
          url,
        });
        toast.success("Shared!");
      } else {
        await shareVideo({
          url,
          title: socialTitle,
          hashtags: socialHashtags,
          mode: guides === "9:16" ? "tiktok" : "full",
        });
      }
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        console.error("Share failed:", e);
      }
    }
  };

  const retry = () => {
    if (!url) return;
    setProbe({ ok: false });
    setLoadError(null);
    setVersion((n) => n + 1);
    toast.success("Retrying...");
  };

  const canEmbed = !isProbing && (probe.ok || !probe.status || !!url);

  const wrapperStyle: React.CSSProperties | undefined =
    guides === "9:16"
      ? { aspectRatio: "9/16", position: "relative" }
      : guides === "16:9"
        ? { aspectRatio: "16/9", position: "relative" }
        : { position: "relative" };

  return (
    <div className="p-6 max-w-4xl mx-auto text-slate-900 dark:text-slate-100">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400">
          <Play size={20} />
        </div>
        <h1 className="text-3xl font-bold">{socialTitle}</h1>
      </div>

      {!url ? (
        <div className="card p-8 text-center space-y-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 mb-2">
            <AlertCircle size={32} />
          </div>
          <h2 className="text-xl font-semibold text-red-600 dark:text-red-300">
            No video URL provided
          </h2>
          <p className="text-slate-600 dark:text-slate-400">
            Please generate a video first or provide a valid video URL.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline font-medium"
          >
            ← Go back to Home
          </Link>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Status banner */}
          {isProbing && (
            <div className="card p-3 flex items-center gap-2 text-slate-600 dark:text-slate-400">
              <div className="animate-spin">
                <RotateCw size={16} />
              </div>
              <span className="text-sm">Checking video availability...</span>
            </div>
          )}

          {!isProbing && !probe.ok && probe.message && (
            <div className="card p-3 flex items-center gap-2 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-900/30">
              <AlertCircle size={16} />
              <span className="text-sm">
                {probe.message} {probe.status && `(HTTP ${probe.status})`}
              </span>
            </div>
          )}

          {/* Video player or fallback */}
          {canEmbed && !loadError ? (
            <div
              className="rounded-xl overflow-hidden bg-black shadow-lg"
              style={wrapperStyle}
            >
              <video
                key={version}
                ref={videoRef}
                className="block w-full h-full object-cover"
                src={url}
                controls
                preload="metadata"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onLoadedMetadata={() => {
                console.log("loaded metadata", url);
              }}
              onCanPlay={() => {
                console.log("can play", url);
              }}
              onPlaying={() => {
                console.log("playing", url);
              }}
              onError={(e) => {
                console.error("video element error", e, videoRef.current?.currentSrc);
              }}
              >
                Your browser does not support the video tag.
              </video>

              {/* Safe zone overlay */}
              {guides !== "off" && (
                <SafeZoneOverlay format={guides === "9:16" ? "9:16" : "16:9"} />
              )}
            </div>
          ) : loadError ? (
            <div className="card p-6 text-center space-y-4">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 mb-2">
                <AlertCircle size={24} />
              </div>
              <h3 className="font-semibold text-lg">Couldn't load video</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 max-w-md mx-auto">
                {loadError}. You can still open or download the video directly.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10 inline-flex items-center gap-2"
                >
                  <ExternalLink size={16} />
                  Open in new tab
                </a>
                <a
                  href={url}
                  download
                  className="btn bg-green-600 hover:bg-green-700 text-white inline-flex items-center gap-2"
                >
                  <Download size={16} />
                  Download
                </a>
                <button
                  onClick={retry}
                  className="btn border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10 inline-flex items-center gap-2"
                >
                  <RotateCw size={16} />
                  Retry
                </button>
              </div>
            </div>
          ) : null}

          {/* Controls */}
          <div className="card p-4 space-y-4">
            {/* Guide mode selector */}
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Safe Zones:
              </span>
              {(["off", "9:16", "16:9"] as GuideMode[]).map((g) => (
                <button
                  key={g}
                  onClick={() => setGuides(g)}
                  className={[
                    "px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors",
                    guides === g
                      ? "bg-blue-600 text-white border-blue-600 dark:bg-blue-500 dark:border-blue-500"
                      : "border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10",
                  ].join(" ")}
                  aria-pressed={guides === g}
                  title={
                    g === "9:16"
                      ? "TikTok safe zones + crop"
                      : g === "16:9"
                        ? "YouTube safe zones + crop"
                        : "Hide guides"
                  }
                >
                  {g === "off" ? "Off" : g}
                </button>
              ))}
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setEditOpen(true)}
                className="btn border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10 inline-flex items-center gap-2"
                title="Re-render with different layout/explanation"
              >
                <Edit3 size={16} />
                Edit Format
              </button>

              <Link
                to={`/renderEditor?url=${encodeURIComponent(url)}`}
                className="btn border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10 inline-flex items-center gap-2"
                title="Open advanced Render Editor"
              >
                <ExternalLink size={16} />
                Open in Editor
              </Link>

              <button
                onClick={handleCopy}
                className="btn btn-primary inline-flex items-center gap-2"
                title="Copy URL (⌘C)"
              >
                <Copy size={16} />
                Copy URL
              </button>

              <button
                onClick={handleShare}
                className="btn btn-secondary inline-flex items-center gap-2"
                title="Share video"
              >
                <Share2 size={16} />
                Share
              </button>

              <a
                href={url}
                download
                className="btn bg-green-600 hover:bg-green-700 text-white inline-flex items-center gap-2"
                title="Download video"
                onClick={() => { if (typeof gtag !== "undefined") gtag("event", "video_downloaded"); }}
              >
                <Download size={16} />
                Download
              </a>

              <button
                onClick={retry}
                className="btn border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10 inline-flex items-center gap-2"
                title="Retry loading video"
              >
                <RotateCw size={16} />
                Retry
              </button>
            </div>

            {/* Tip */}
            <div className="text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-black/5 dark:border-white/5">
              <strong>💡 Tip:</strong> Press <kbd className="px-1.5 py-0.5 rounded bg-white dark:bg-slate-700 border border-black/10 dark:border-white/10 font-mono text-xs">Space</kbd> to play/pause,
              or <kbd className="px-1.5 py-0.5 rounded bg-white dark:bg-slate-700 border border-black/10 dark:border-white/10 font-mono text-xs">⌘C</kbd> to copy URL.
              {guides !== "off" && " Safe zones show where platform UI may overlap your content."}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Format editor */}
      {url && (
        <FormatEditor
          open={editOpen}
          onClose={() => setEditOpen(false)}
          videoUrl={url}
          videoFormat={guides === "9:16" ? "9:16" : "16:9"}
          initial={{
            explanation_mode: guides === "9:16" ? "answer_only" : "full"
          }}
          onRendered={(newUrl) => {
            setUrl(newUrl);
            setProbe({ ok: false });
            setLoadError(null);
            setVersion((n) => n + 1);
            toast.success("✓ Loaded new render");
            setEditOpen(false);
          }}
        />
      )}
    </div>
  );
}